import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SubscriptionButton from './SubscriptionButton';
import AIGeneration from './AIGeneration';

// Переводы
const TRANSLATIONS = {
  ru: {
    selectCharacter: "Выберите персонажа",
    description: "С кем вы хотели бы пообщаться? Каждый персонаж имеет свой характер и стиль общения.",
    newWay: "🔄 Новый способ общения!",
    newWayDescription: "Теперь каждый персонаж имеет свой отдельный чат в Telegram! После выбора персонажа вы перейдете в отдельный чат с ним, где сможете общаться напрямую с уведомлениями и историей сообщений.",
    messagesToday: "Сообщений сегодня",
    characterUnavailable: "недоступен для вашего тарифа. Обновите подписку для доступа к большему количеству персонажей.",
    botAvailable: "✅ Бот доступен - общение в Telegram",
    botUnavailable: "❌ Недоступен для вашего тарифа",
    startChat: "💬 Начать общение",
    creatingTransition: "Создание перехода...",
    locked: "🔒 Заблокировано",
    howItWorks: "📱 Как это работает:",
    step1: "Выберите персонажа",
    step1Desc: "Нажмите на персонажа, с которым хотите общаться",
    step2: "Переход в Telegram",
    step2Desc: "Откроется чат с выбранным персонажем в Telegram",
    step3: "Общайтесь!",
    step3Desc: "Пишите сообщения и получайте ответы",
    backToAgeVerification: "← Назад к проверке возраста",
    loadingCharacters: "Загрузка персонажей...",
    moreDetails: "Подробнее",
    transitionCreated: "Переход к {name} создан! Откройте чат с ботом в Telegram.",
    ok: "OK"
  },
  en: {
    selectCharacter: "Choose Character",
    description: "Who would you like to chat with? Each character has their own personality and communication style.",
    newWay: "🔄 New way of communication!",
    newWayDescription: "Now each character has their own separate chat in Telegram! After selecting a character, you'll be redirected to a separate chat with them, where you can communicate directly with notifications and message history.",
    messagesToday: "Messages today",
    characterUnavailable: "is not available for your subscription. Upgrade your subscription to access more characters.",
    botAvailable: "✅ Bot available - chat in Telegram",
    botUnavailable: "❌ Not available for your subscription",
    startChat: "💬 Start chatting",
    creatingTransition: "Creating transition...",
    locked: "🔒 Locked",
    howItWorks: "📱 How it works:",
    step1: "Choose character",
    step1Desc: "Click on the character you want to chat with",
    step2: "Transition to Telegram",
    step2Desc: "A chat with the selected character will open in Telegram",
    step3: "Chat!",
    step3Desc: "Write messages and get responses",
    backToAgeVerification: "← Back to age verification",
    loadingCharacters: "Loading characters...",
    moreDetails: "More details",
    transitionCreated: "Transition to {name} created! Open chat with the bot in Telegram.",
    ok: "OK"
  }
};

const CHARACTER_SLUG_OVERRIDES = {
  bianka_g: 'Bianca_G',
};

function CharacterSelection({ user, userStatus, characters, onCharacterSelected, createBotTransition, language, onLanguageChange }) {
  const navigate = useNavigate();
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState(null);
  const [availableCharacters, setAvailableCharacters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalCharacter, setModalCharacter] = useState(null);
  const [deepLink, setDeepLink] = useState(null);

  // Функция для получения перевода
  const t = (key) => TRANSLATIONS[language][key] || key;

  const loadSubscriptionLimits = useCallback(async () => {
    try {
      const response = await axios.get(`/subscription_limits?initData=${encodeURIComponent(user.initData)}`);
      setAvailableCharacters(response.data.available_characters);
    } catch (error) {
      console.error('Failed to load subscription limits:', error);
      // Fallback - показываем всех персонажей
      setAvailableCharacters(Object.keys(characters));
    }
  }, [user?.initData, characters]);

  useEffect(() => {
    if (user?.initData) {
      loadSubscriptionLimits();
    }
  }, [user?.initData, loadSubscriptionLimits]);

  const setUserLanguage = async (lang) => {
    try {
      await axios.post('/set_language', {
        initData: user.initData,
        language: lang
      });
    } catch (error) {
      console.error('Failed to set user language:', error);
    }
  };

  const selectCharacter = async (characterId) => {
    if (!user?.initData) return;

    // Проверяем, доступен ли персонаж для текущей подписки
    if (availableCharacters.length > 0 && !availableCharacters.includes(characterId)) {
      setError(`${characters[characterId]?.name} ${t('characterUnavailable')}`);
      return;
    }

    setSelecting(true);
    setError(null);

    try {
      // Получаем информацию о персонаже
      const character = characters[characterId];
      if (!character) {
        throw new Error('Character not found');
      }

      // Сначала устанавливаем язык пользователя
      await setUserLanguage(language);

      // Создаем deep link напрямую
      const link = `https://t.me/${character.username}?start=user_${extractUserId(user.initData)}`;
      setDeepLink(link);
      
      // Показываем кастомное модальное окно
      setModalCharacter(character);
      setShowModal(true);
      
      // Обновляем статус пользователя
      if (onCharacterSelected) {
        await onCharacterSelected();
      }

    } catch (error) {
      console.error('Character selection error:', error);
      setError('Error selecting character. Please try again.');
    } finally {
      setSelecting(false);
    }
  };

  // Извлекаем user_id из initData
  const extractUserId = (initData) => {
    try {
      const data = typeof initData === 'string' ? JSON.parse(initData) : initData;
      if (data.result?.initData?.user?.id) {
        return data.result.initData.user.id;
      }
      return 'test'; // Fallback для тестирования
    } catch (error) {
      console.error('Error extracting user ID:', error);
      return 'test';
    }
  };

  if (Object.keys(characters).length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy-500 mx-auto mb-4"></div>
          <p className="text-gray-400">{t('loadingCharacters')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Navigation Bar */}
        <nav className="bg-dark-800 border-b border-burgundy-800 mb-4">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                <img 
                  src={`${process.env.NODE_ENV === 'production' ? 'https://hotbump.online' : 'http://localhost:8010'}/webava.png`}
                  alt="HotBump" 
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <span className="text-xl font-bold">HotBump</span>
              </div>

              {/* Right Side */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* Link to Web version */}
                <a 
                  href="/web/explore" 
                  className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium text-gray-300 hover:text-burgundy-400 hover:bg-dark-700 transition"
                >
                  <span className="hidden md:inline">{language === 'en' ? 'Chat on website' : 'Общение на сайте'}</span>
                  <span className="md:hidden">{language === 'en' ? 'Website' : 'Сайт'}</span>
                </a>
                
                {/* Language Switcher */}
                <select
                  value={language}
                  onChange={(e) => onLanguageChange(e.target.value)}
                  className="px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium bg-dark-700 border border-burgundy-800 text-white focus:outline-none focus:ring-2 focus:ring-burgundy-600"
                >
                  <option value="en">EN</option>
                  <option value="ru">RU</option>
                </select>
              </div>
            </div>
          </div>
        </nav>

        {/* Кнопка подписки - на всю ширину */}
        <div className="mb-4">
          <SubscriptionButton 
            user={user} 
            userStatus={userStatus} 
            onSubscriptionUpdate={onCharacterSelected}
            fullWidth={true}
            language={language}
          />
        </div>

        {/* Заголовок - на всю ширину */}
        <div className="bg-dark-800 border border-burgundy-900 rounded-lg shadow-lg p-5 mb-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-100">
              {t('selectCharacter')}
            </h1>
          </div>
        </div>

        {/* AI Generation Section */}
        <AIGeneration user={user} language={language} />

        {/* Описательный текст - на всю ширину */}
        <div className="bg-dark-800 border border-burgundy-900 rounded-lg shadow-lg p-5 mb-6">
          <p className="text-gray-300 text-center mb-3">
            {t('description')}
          </p>
          
          <div className="bg-burgundy-900 bg-opacity-30 border border-burgundy-700 rounded-lg p-4">
            <h3 className="font-bold text-burgundy-400 mb-2 text-center">{t('newWay')}</h3>
            <p className="text-sm text-gray-300 text-center">
              {t('newWayDescription')}
            </p>
          </div>
        </div>

        {/* Счетчик сообщений */}
        {userStatus && (
          <div className="bg-dark-800 border border-burgundy-900 rounded-lg shadow-lg p-4 mb-6">
            <p className="text-sm text-gray-300 text-center">
              {t('messagesToday')}: <span className="text-burgundy-400 font-bold">{userStatus.daily_messages}</span> / <span className="text-burgundy-400">{userStatus.daily_limit === -1 ? '∞' : userStatus.daily_limit}</span>
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-burgundy-900 bg-opacity-50 border border-burgundy-600 text-burgundy-400 rounded-lg">
            {error}
          </div>
        )}

        {/* Characters grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(characters).map(([id, character]) => {
            const isAvailable = availableCharacters.length === 0 || availableCharacters.includes(id);
            const isLocked = !isAvailable;
            
            const detailSlug =
              CHARACTER_SLUG_OVERRIDES[id] ||
              (character?.name
                ? character.name.replace(/\s+/g, '_')
                : id.replace(/-/g, '_'));

            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/characters/${detailSlug}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/characters/${detailSlug}`);
                  }
                }}
                className={`bg-dark-800 rounded-lg shadow-2xl overflow-hidden hover:shadow-burgundy-500/20 transition-all border border-burgundy-900 flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-burgundy-600 ${
                  isLocked ? '' : 'hover:border-burgundy-700'
                }`}
              >
                {/* Character avatar */}
                <div className="h-48 bg-gradient-to-br from-black via-burgundy-900 to-burgundy-800 flex items-center justify-center relative">
                  {isLocked && (
                    <div className="absolute top-2 right-2 z-10 bg-black bg-opacity-50 rounded-full p-2 flex items-center justify-center">
                      <div className="text-white text-xl">🔒</div>
                    </div>
                  )}
                  <div className="w-24 h-24 bg-dark-700 rounded-full flex items-center justify-center overflow-hidden border-2 border-burgundy-700 shadow-lg">
                    {character.avatar ? (
                      <img 
                        src={`${process.env.NODE_ENV === 'production' ? 'https://hotbump.online' : 'http://localhost:8010'}${character.avatar}`}
                        alt={character.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span className="text-3xl" style={{display: character.avatar ? 'none' : 'block'}}>👤</span>
                  </div>
                </div>

                {/* Character info */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    {character.name}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {typeof character.description === 'object' 
                      ? (character.description[language] || character.description.ru)
                      : character.description}
                  </p>

                  {/* Bot status indicator */}
                  <div className={`mb-4 p-2 rounded-lg ${isAvailable ? 'bg-burgundy-900 bg-opacity-30 border border-burgundy-700' : 'bg-dark-700 border border-gray-700'}`}>
                    <p className={`text-xs ${isAvailable ? 'text-burgundy-400' : 'text-gray-500'}`}>
                      {isAvailable ? t('botAvailable') : t('botUnavailable')}
                    </p>
                  </div>

                  {/* Select button */}
                  <div className="mt-auto space-y-3">
                    <Link
                      to={`/characters/${detailSlug}`}
                      className="block w-full text-center font-semibold py-2.5 px-4 rounded-lg border border-burgundy-700 text-burgundy-300 hover:bg-burgundy-700/10 transition-all duration-200"
                    >
                      {t('moreDetails')}
                    </Link>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        selectCharacter(id);
                      }}
                      disabled={selecting || isLocked}
                      className={`w-full font-bold py-3 px-4 rounded-lg transition-all duration-200 ${
                        isLocked 
                          ? 'bg-dark-700 text-gray-600 cursor-not-allowed' 
                          : 'bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-700 text-white shadow-lg hover:shadow-burgundy-500/50'
                      }`}
                    >
                      {selecting ? t('creatingTransition') : isLocked ? t('locked') : t('startChat')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-dark-800 rounded-lg shadow-2xl p-6 border border-burgundy-900">
          <h3 className="text-lg font-bold text-gray-100 mb-4">{t('howItWorks')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-burgundy-900 bg-opacity-50 rounded-full flex items-center justify-center mx-auto mb-2 border border-burgundy-700">
                <span className="text-xl">1️⃣</span>
              </div>
              <h4 className="font-bold text-gray-100 mb-1">{t('step1')}</h4>
              <p className="text-sm text-gray-400">{t('step1Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-burgundy-900 bg-opacity-50 rounded-full flex items-center justify-center mx-auto mb-2 border border-burgundy-700">
                <span className="text-xl">2️⃣</span>
              </div>
              <h4 className="font-bold text-gray-100 mb-1">{t('step2')}</h4>
              <p className="text-sm text-gray-400">{t('step2Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-burgundy-900 bg-opacity-50 rounded-full flex items-center justify-center mx-auto mb-2 border border-burgundy-700">
                <span className="text-xl">3️⃣</span>
              </div>
              <h4 className="font-bold text-gray-100 mb-1">{t('step3')}</h4>
              <p className="text-sm text-gray-400">{t('step3Desc')}</p>
            </div>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/age-verification')}
            className="text-gray-500 hover:text-burgundy-400 underline transition-colors"
          >
            {t('backToAgeVerification')}
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && modalCharacter && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border-2 border-burgundy-700 rounded-lg shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-100">hotbump.online</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Message */}
            <p className="text-gray-300 mb-6">
              {t('transitionCreated').replace('{name}', modalCharacter.name)}
            </p>

            {/* OK Button */}
            <button
              onClick={() => {
                setShowModal(false);
                setTimeout(() => {
                  if (window.Telegram?.WebApp?.openTelegramLink) {
                    window.Telegram.WebApp.openTelegramLink(deepLink);
                  } else {
                    window.location.href = deepLink;
                  }
                }, 100);
              }}
              className="w-full bg-burgundy-700 hover:bg-burgundy-600 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-burgundy-500/50"
            >
              {t('ok')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CharacterSelection;
